import React, { useEffect, useState, useReducer, createContext } from "react";
import axios from "axios";

import { Grid } from "@mui/material";

import userReducer from "../reducers/user-reducer";
import introducerReducer from "../reducers/introducer-reducer";
import currencyReducer from "../reducers/currency-reducer";

import { setUser, setIntroducer, setCurrency } from "../actions";

import { useQuery } from "@apollo/client";
import { GET_PAYMENT_CURRENCIES } from "../../apollo/queries";

import getCountryCurrencyCode from "../../functions/getCountryCurrencyCode";

import Logo from "../../images/Logo";
import SpinningLogo from "../../components/ui/spinning-logo";

export const UserContext = createContext();
const UserProvider = UserContext.Provider;

export const IntroducerContext = createContext();
export const CurrencyContext = createContext();

export function UserWrapper({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const guestUserID = process.env.GATSBY_GUEST_ID ?? 0;

  // eslint-disable-next-line no-unused-vars
  const [defaultUser, setDefaultUser] = useState({
    id: Number(guestUserID),
    username: "Guest",
    email: "guest@chikitea.com",
    country: null,
  });

  const storedUser =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("user"))
      : null;

  const [user, dispatchUser] = useReducer(
    userReducer,
    storedUser || defaultUser
  );

  const defaultIntroducer = { introducedByCode: null };
  const defaultCurrency = {
    currency: "UK Pounds",
    codeISO: "GBP",
    country: "United Kingdom",
  };

  const storedIntroducer =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("introducer"))
      : null;

  // eslint-disable-next-line no-unused-vars
  const [introducer, dispatchIntroducer] = useReducer(
    introducerReducer,
    storedIntroducer || defaultIntroducer
  );

  const storedCurrency =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("currency"))
      : null;

  // eslint-disable-next-line no-unused-vars
  const [currency, dispatchCurrency] = useReducer(
    currencyReducer,
    storedCurrency || defaultCurrency
  );

  const { loading, data } = useQuery(GET_PAYMENT_CURRENCIES, {
    fetchPolicy: "network-only", // Always fetch from the network
  });

  // Helper function to fetch location and update user
  const fetchLocationAndUpdateUser = async (paymentCurrencies, updatedUser) => {
    try {
      // Add a simple caching mechanism to reduce API calls
      const cachedLocation = sessionStorage.getItem("userLocation");
      let locationData;

      if (cachedLocation) {
        // Use cached data if available
        locationData = JSON.parse(cachedLocation);
      } else {
        // Use a timeout to prevent long-hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

        const response = await axios.get("https://api.ipbase.com/v1/json/", {
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.status === 200 && response.data) {
          locationData = response.data;
          // Cache the result
          sessionStorage.setItem("userLocation", JSON.stringify(locationData));
        }
      }

      if (locationData && locationData.country_name) {
        const country = locationData.country_name;
        const currencyCode = getCountryCurrencyCode(country);

        console.log("Detected country:", country);
        console.log("Detected currency code:", currencyCode);

        // Find the currency name from the code
        const currency = paymentCurrencies.find(
          (paymentCurrency) =>
            paymentCurrency.attributes.allowPayments &&
            paymentCurrency.attributes.codeISO === currencyCode
        )?.attributes.name;

        const updatedCurrency = currency
          ? {
              codeISO: currencyCode,
              currency,
              country,
            }
          : defaultCurrency;

        dispatchCurrency(setCurrency(updatedCurrency));

        const userWithLocation = {
          ...updatedUser,
          country,
          currency: updatedCurrency.currency,
          codeISO: updatedCurrency.codeISO,
        };

        dispatchUser(setUser(userWithLocation));
      } else {
        // If we couldn't get location data, just use the default
        console.warn("Could not determine location, using default settings");
        dispatchUser(setUser(updatedUser));
      }
    } catch (error) {
      // More detailed error handling
      if (error.code === "ERR_BAD_REQUEST" && error.response?.status === 429) {
        console.warn("Location API rate limit reached, using default settings");
      } else if (error.name === "AbortError") {
        console.warn("Location request timed out, using default settings");
      } else {
        console.error("Error fetching location:", error);
      }

      // Continue with what we have
      dispatchUser(setUser(updatedUser));
    }
  };

  // Helper function to handle loyalty tier
  const handleLoyaltyTier = (storedUser, responseData) => {
    const loyaltyTier = storedUser?.loyaltyTier || null;

    return {
      ...responseData,
      loyaltyTier,
      jwt: storedUser.jwt,
      onboarding: true,
    };
  };

  // Main useEffect
  useEffect(() => {
    if (!loading && data) {
      const paymentCurrencies = data.currencies.data;

      // Extract URL parameters
      const params = new URLSearchParams(window.location.search);
      const introducedByCode = params.get("introducer");
      const code = params.get("code");
      const provider = params.get("provider");
      const isSocialSignInOrPasswordReset = code || provider;

      if (!isSocialSignInOrPasswordReset) {
        let currentUser = storedUser || defaultUser;
        let updatedUser = { ...currentUser };

        // Handle introducer logic
        if (introducedByCode) {
          updatedUser = { ...updatedUser, introducedByCode };
          dispatchIntroducer(setIntroducer({ introducedByCode }));
        } else if (storedIntroducer?.introducedByCode) {
          updatedUser = {
            ...updatedUser,
            introducedByCode: storedIntroducer.introducedByCode,
          };
        }

        // Fetch location and update user
        if (!storedUser?.country) {
          // Use setTimeout to defer the API call until after initial render
          setTimeout(() => {
            fetchLocationAndUpdateUser(paymentCurrencies, updatedUser);
          }, 2000); // 2 second delay
        } else {
          // If we already have a country, just update the user directly
          dispatchUser(setUser(updatedUser));
        }

        // Handle stored user logic
        if (storedUser) {
          setTimeout(() => {
            axios
              .get(process.env.GATSBY_STRAPI_API_URL + "/api/users/me", {
                headers: {
                  Authorization: `Bearer ${storedUser.jwt}`,
                },
                populate: "*",
              })
              .then((response) => {
                const updatedUserWithLoyalty = handleLoyaltyTier(
                  storedUser,
                  response.data
                );
                dispatchUser(setUser(updatedUserWithLoyalty));
                setIsLoading(false);
              })
              .catch((error) => {
                console.error("Error fetching stored user:", error);
                dispatchUser(setUser(updatedUser));
                setIsLoading(false);
              });
          }, 1500);
        } else {
          dispatchUser(setUser(updatedUser));
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  const contextValue = React.useMemo(
    () => ({ user, dispatchUser, defaultUser }),
    [user, defaultUser]
  );

  return (
    <UserProvider value={contextValue}>
      {isLoading ? (
        <Grid
          item
          container
          justifyContent="center"
          alignItems="flex-start"
          backgroundColor="primary.veryDark"
          height={2400}
        >
          <Grid item marginTop={22}>
            <SpinningLogo Logo={Logo} size="150" color="#fff" />
          </Grid>
        </Grid>
      ) : (
        children
      )}
    </UserProvider>
  );
}
